import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaBell, FaBug } from 'react-icons/fa';
import { useAppContext } from '../../context/AppContext';
import ReportProblemModal from '../Modals/ReportProblemModal/ReportProblemModal';
import TourButton from '../TourButton/TourButton';
import { getTopNavbarSteps } from '../../tours/topNavbar';
import './TopNavbar.css';

/**
 * Route-to-translation-key map used to derive the page title from the current
 * URL pathname. Keys are matched with `startsWith` so nested paths (e.g.
 * `/client/123`) are covered by a prefix entry.
 *
 * @constant {Array<{prefix: string, key: string}>}
 */
const ROUTE_TITLES = [
    { prefix: '/home',            key: 'Home'               },
    { prefix: '/clients',         key: 'Clients'             },
    { prefix: '/client/',         key: 'client-details'      },
    { prefix: '/calendar',        key: 'Calendar'            },
    { prefix: '/foods',           key: 'foods-sidebar-text'  },
    { prefix: '/templates',        key: 'Templates'           },
    { prefix: '/settings',        key: 'Settings'            },
    { prefix: '/profile',         key: 'profile'             },
];

/**
 * TopNavbar component.
 *
 * Fixed top navigation bar rendered on all authenticated pages (the same
 * routes that show the sidebar). Contains:
 * - **Left** — current page title, derived automatically from the URL.
 * - **Right** — notification bell icon (functionality deferred) and the
 *   dietist's profile avatar (initials fallback while no image is set).
 *
 * @component
 * @returns {JSX.Element}
 */
const TopNavbar = () => {
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const [showReportModal, setShowReportModal] = React.useState(false);
    const tourSteps = React.useMemo(() => getTopNavbarSteps(t), [t]);

    /** @type {{ dietist: object }} */
    const { dietist } = useAppContext();

    /**
     * Resolves the translated page title for the current route.
     * Falls back to an empty string if no prefix matches.
     *
     * @type {string}
     */
    const pageTitle = (() => {
        const match = ROUTE_TITLES.find(r => location.pathname.startsWith(r.prefix));
        return match ? t(match.key) : '';
    })();

    /**
     * One or two uppercase initials derived from the dietist's name and
     * surname, used as the avatar fallback when no profile image is available.
     *
     * @type {string}
     */
    const initials = dietist
        ? `${dietist.name?.[0] ?? ''}${dietist.surname?.[0] ?? ''}`.toUpperCase()
        : '';

    return (
        <header className="tnb">

            {/* ── Left: page title (desktop) / brand name (mobile) ── */}
            <h1 className="tnb-title">{pageTitle}</h1>
            <span className="tnb-brand">DietistPro</span>

            {/* ── Right: actions ── */}
            <div className="tnb-actions">

                {/* Tour button */}
                <TourButton steps={tourSteps} iconOnly />

                {/* Report a problem */}
                <button
                    className="tnb-notif-btn"
                    aria-label={t('rpm-title')}
                    title={t('rpm-title')}
                    onClick={() => setShowReportModal(true)}
                    data-tour="tnb-bug-report"
                >
                    <FaBug className="tnb-notif-icon" />
                </button>
                
                {/* Notification bell — functionality deferred */}
                {/*
                <button className="tnb-notif-btn" aria-label={t('notifications')}>
                    <FaBell className="tnb-notif-icon" />
                </button>
                */}

                {/* Profile avatar — navigates to profile page */}
                <div
                    className="tnb-avatar tnb-avatar--desktop-only"
                    role="button"
                    tabIndex={0}
                    aria-label={t('profile')}
                    onClick={() => navigate('/profile')}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') navigate('/profile'); }}
                    data-tour="tnb-profile-avatar"
                >
                    <img
                        src={dietist?.profileImage || '/images/anonymous_avatar_icon.jpg'}
                        alt={initials}
                        className="tnb-avatar-img"
                    />
                </div>

            </div>

            {showReportModal && <ReportProblemModal onClose={() => setShowReportModal(false)} />}
        </header>
    );
};

export default TopNavbar;
